import * as React from 'react';
import { CreativeStatus } from '../../../enums/ItemStatus';
import { Metadata } from '../../../types/Metadata';
import { WorkflowInfo } from '../../../types/WorkflowInfo';
import { isAuthorPromotableAtConceptReview } from '../../../utils';

type Params = {
  empCd: number;
  leadAuthorEmpCd: number;
  itemCreativeStatusId: Metadata;
  workflowInfo: WorkflowInfo;
  isBackupTM: boolean;
  isAdmin: boolean;
  isWorkspaceOwner: boolean;
  isWorkspacePlanner?: boolean;
  contentTypeId?: number;
  itemStatusId?: number;
  showExternalReview?: boolean;
};

export const useConfigureWorkflowPermissions = ({
  empCd,
  leadAuthorEmpCd,
  itemCreativeStatusId,
  workflowInfo,
  isBackupTM,
  isAdmin,
  isWorkspaceOwner,
  isWorkspacePlanner = false,
  contentTypeId,
  itemStatusId,
  showExternalReview = false
}: Params) => {
  const authorAtConceptReview = isAuthorPromotableAtConceptReview(
    empCd,
    leadAuthorEmpCd,
    contentTypeId,
    itemStatusId
  );
  const canConfigureCreativeHelp = React.useMemo(() => {
    return (
      authorAtConceptReview ||
      (empCd == leadAuthorEmpCd &&
        (itemCreativeStatusId?.props?.id == CreativeStatus.PromoteToCreative ||
          itemCreativeStatusId?.props?.id == CreativeStatus.CreativeIntake))
    );
  }, [empCd, leadAuthorEmpCd, itemCreativeStatusId, authorAtConceptReview]);
  const canConfigureWorkflow = React.useMemo(() => {
    return (
      isWorkspaceOwner ||
      isBackupTM ||
      isAdmin ||
      workflowInfo?.teamManagerIds?.includes(empCd) ||
      authorAtConceptReview
    );
  }, [
    isWorkspaceOwner,
    isBackupTM,
    isAdmin,
    workflowInfo,
    empCd,
    authorAtConceptReview
  ]);

  const canSaveWorkflow = isWorkspacePlanner || authorAtConceptReview;
  const canConfigureExternalReview = React.useMemo(
    () =>
      showExternalReview && (canConfigureWorkflow || empCd === leadAuthorEmpCd),
    [showExternalReview, canConfigureWorkflow, empCd, leadAuthorEmpCd]
  );

  return {
    canConfigureCreativeHelp,
    canConfigureWorkflow,
    canSaveWorkflow,
    canConfigureExternalReview
  };
};

export default useConfigureWorkflowPermissions;
